import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CssBaseline } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SnackbarProvider } from "notistack";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./routes/ProtectedRoute";
import MainLayout from "./layouts/MainLayout";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import TicketsHome from "./pages/tickets/TicketsHome";
import TicketBoard from "./pages/tickets/TicketBoard";
import TicketListView from "./pages/tickets/TicketListView";
import TicketBacklog from "./pages/tickets/TicketBacklog";
import TicketPlanBoard from "./pages/tickets/TicketPlanBoard";
import TicketDetail from "./pages/tickets/TicketDetail";
import RolesAndTeamsPage from "./pages/admin/RolesAndTeamsPage";
import EmployeesPage from "./pages/admin/EmployeesPage";
import SlaSettingsPage from "./pages/admin/SlaSettingsPage";
import WorkflowSettingsPage from "./pages/admin/WorkflowSettingsPage";
import AutomationPage from "./pages/admin/AutomationPage";
import WebhooksPage from "./pages/admin/WebhooksPage";
import IntegrationsPage from "./pages/admin/IntegrationsPage";
import ProjectsPage from "./pages/projects/ProjectsPage";
import ReportsPage from "./pages/reports/ReportsPage";
import KbPage from "./pages/kb/KbPage";
import KbArticleDetail from "./pages/kb/KbArticleDetail";
import AssetsPage from "./pages/assets/AssetsPage";
import NotificationsPage from "./pages/notifications/NotificationsPage";
import SearchPage from "./pages/search/SearchPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <CssBaseline />
        <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <AuthProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<Login />} />

                <Route
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={<Dashboard />} />

                  <Route path="/tickets" element={<TicketsHome />} />
                  <Route path="/tickets/p/:projectId/board" element={<TicketBoard />} />
                  <Route path="/tickets/p/:projectId/list" element={<TicketListView />} />
                  <Route path="/tickets/p/:projectId/backlog" element={<TicketBacklog />} />
                  <Route path="/tickets/p/:projectId/plans/:planId/board" element={<TicketPlanBoard />} />
                  <Route path="/tickets/:ticketId" element={<TicketDetail />} />

                  <Route path="/admin/employees" element={<EmployeesPage />} />
                  <Route path="/admin/roles" element={<RolesAndTeamsPage />} />
                  <Route path="/admin/sla" element={<SlaSettingsPage />} />
                  <Route path="/admin/workflow" element={<WorkflowSettingsPage />} />
                  <Route path="/admin/automation" element={<AutomationPage />} />
                  <Route path="/admin/webhooks" element={<WebhooksPage />} />
                  <Route path="/admin/integrations" element={<IntegrationsPage />} />

                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />

                  <Route path="/kb" element={<KbPage />} />
                  <Route path="/kb/:articleId" element={<KbArticleDetail />} />
                  <Route path="/assets" element={<AssetsPage />} />
                  <Route path="/search" element={<SearchPage />} />

                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </Route>

                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}